import React, { useState } from 'react';
import ValidadorResultado from '../Components/ValidadorResultado';
import Api from '../Auth/Api';
import HeaderPublic from '../Layout/HeaderPublic';
import ContentPublic from '../Components/ContentPublic';
import './Validador.css';

const LOADING_MESSAGES = [
    'Pesquisando matrícula...',
    'Localizando certificados...',
    'Carregando dados do participante...',
    'Recuperando informações complementares...',
    'Consolidando dados...',
    'Validando autenticidade...',
    'Certificado encontrado!'
];

const getRandomStepDelay = () => Math.floor(Math.random() * 1000) + 500; // 0.5s a 1.5s
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const FINAL_STEP_HOLD_MS = 700; // garante render visível da última etapa

function Validador() {
    const [codigo, setCodigo] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0);
    const [resultado, setResultado] = useState(null);
    const [erro, setErro] = useState('');

    const runLoadingSteps = async () => {
        for (let step = 1; step < LOADING_MESSAGES.length; step++) {
            await wait(getRandomStepDelay());
            setLoadingStep(step);
        }
        await wait(FINAL_STEP_HOLD_MS);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!codigo.trim()) return;

        setLoading(true);
        setLoadingStep(0);
        setErro('');
        setResultado(null);
        let res = null;
        let err = '';

        const apiPromise = (async () => {
            try {
                const codigoLimpo = String(codigo || '').trim();
                const pareceMatricula = /^\d{5,}$/.test(codigoLimpo);
                const tentativas = pareceMatricula
                    ? [
                          { endpoint: `public/certificados-matricula/${codigoLimpo}`, tipo: 'matricula' },
                          { endpoint: `public/validar-certificado/${codigoLimpo}`, tipo: 'certificado' }
                      ]
                    : [
                          { endpoint: `public/validar-certificado/${codigoLimpo}`, tipo: 'certificado' },
                          { endpoint: `public/certificados-matricula/${codigoLimpo}`, tipo: 'matricula' }
                      ];

                for (const tentativa of tentativas) {
                    try {
                        const response = await Api.get(tentativa.endpoint);
                        res = { tipo: tentativa.tipo, data: response.data };
                        return;
                    } catch (requestError) {
                        if (requestError.response?.status !== 404) {
                            throw requestError;
                        }
                    }
                }

                err = 'Certificado ou matrícula não encontrado.';
            } catch (error) {
                console.error('Erro ao validar:', error);
                err = error.response?.status === 404
                    ? 'Certificado ou matrícula não encontrado.'
                    : 'Erro ao validar. Tente novamente.';
            }
        })();

        // Só permite transição depois de passar por todas as etapas.
        await runLoadingSteps();

        await apiPromise;
        setResultado(res);
        setErro(err || '');
        setLoading(false);
    };

    const handleVoltar = () => {
        setResultado(null);
        setErro('');
        setCodigo('');
    };

    if (resultado) {
        return (
            <ContentPublic>
                <div className="validador-page">
                    <HeaderPublic />
                    <ValidadorResultado 
                    resultado={resultado} 
                    onVoltar={handleVoltar}
                    onValidarCertificado={async (id) => {
                        try {
                            const response = await Api.get(`public/validar-certificado/${id}`);
                            setResultado({ tipo: 'certificado', data: response.data });
                        } catch (error) {
                            setErro('Erro ao carregar detalhes do certificado.');
                        }
                    }}
                />
                </div>
            </ContentPublic>
        );
    }

    return (
        <ContentPublic>
            <div className="validador-page">
                <HeaderPublic />
                <div className="validador-form-wrapper">
                    <div className="validador-form-container">
                        <div className="validador-form-card">
                            <div className="validador-form-header">
                                <div className="validador-form-icon">
                                    <i className="bi bi-shield-check"></i>
                                </div>
                                <h1 className="validador-form-title">Validador de Certificados</h1>
                                <p className="validador-form-subtitle">
                                    Digite o ID do certificado, código de matrícula ou escaneie o QR Code
                                </p>
                            </div>

                            {loading ? (
                                <div className="validador-loading">
                                    <div className="validador-loading-spinner"></div>
                                    <p className={`validador-loading-message ${loadingStep === LOADING_MESSAGES.length - 1 ? 'success' : ''}`} key={loadingStep}>
                                        {LOADING_MESSAGES[loadingStep]}
                                    </p>
                                    <div className="validador-loading-steps">
                                        {LOADING_MESSAGES.map((_, i) => (
                                            <span
                                                key={i}
                                                className={`validador-loading-dot ${i <= loadingStep ? 'active' : ''}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="validador-form">
                                    <div className="validador-form-group">
                                        <label className="validador-form-label">
                                            ID do Certificado ou Matrícula
                                        </label>
                                        <div className="validador-input-wrapper">
                                            <i className="bi bi-upc-scan validador-input-icon"></i>
                                            <input
                                                type="text"
                                                className="validador-input"
                                                placeholder="Digite o ID ou matrícula..."
                                                value={codigo}
                                                onChange={(e) => setCodigo(e.target.value)}
                                                disabled={loading}
                                            />
                                        </div>
                                        <span className="validador-form-hint">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Use o ID do certificado ou o código de matrícula do aluno
                                        </span>
                                    </div>

                                    {erro && (
                                        <div className="validador-alert validador-alert-error">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {erro}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className="validador-btn-submit"
                                        disabled={loading || !codigo.trim()}
                                    >
                                        <i className="bi bi-search me-2"></i>
                                        Validar Certificado
                                    </button>
                                </form>
                            )}

                            <div className="validador-form-footer">
                                <i className="bi bi-shield-lock me-1"></i>
                                Validação segura e confiável
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ContentPublic>
    );
}

export default Validador;
